import { NavLink } from 'react-router'

import { usePermissions } from '@/features/auth'
import { Logo } from '@/shared/components/brand'
import { APP_VERSION } from '@/shared/config/appVersion'
import { cn } from '@/shared/lib/cn'

import { MAIN_MENU, MENU_SECTIONS } from '../router/routes'

/**
 * Barra lateral de navegación.
 *
 * Es el marco de marca permanente de la aplicación: un rail verde profundo con
 * texto crema y el item activo marcado en oro. Se construye a partir de
 * `MAIN_MENU` y se filtra por permisos, así que cada usuario ve solo sus
 * secciones. Agregar un módulo al menú es añadir un objeto en `routes.ts` —
 * este archivo no se toca.
 */
export function Sidebar({ className }: { className?: string }) {
  const { can } = usePermissions()

  const visibleSections = MENU_SECTIONS.map((section) => ({
    ...section,
    items: MAIN_MENU.filter((item) => item.section === section.key && can(item.permission)),
  })).filter((section) => section.items.length > 0)

  return (
    // <nav> en vez de <div>: los lectores de pantalla ofrecen saltar
    // directamente a la navegación. Usar el elemento HTML correcto es la forma
    // más barata de accesibilidad que existe.
    <nav
      className={cn(
        'bg-sidebar text-sidebar-foreground border-sidebar-border flex h-full w-60 flex-col border-r',
        className,
      )}
      aria-label="Navegación principal"
    >
      <div className="border-sidebar-border flex h-16 items-center border-b px-5">
        <Logo variant="horizontal" size={36} className="text-sidebar-foreground" />
      </div>

      <ul className="flex-1 space-y-2 overflow-y-auto p-3">
        {visibleSections.map(({ key, label, items }) => (
          <li key={key}>
            <h2 className="text-sidebar-foreground/50 px-3 pt-2 pb-1 text-xs font-medium tracking-wide first:pt-0">
              {label}
            </h2>
            <ul className="space-y-1">
              {items.map(({ label: itemLabel, path, icon: Icon }) => (
                <li key={path}>
                  <NavLink
                    to={path}
                    className={({ isActive }) =>
                      cn(
                        'relative flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                        'focus-visible:ring-sidebar-ring/60 outline-none focus-visible:ring-2',
                        isActive
                          ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium before:absolute before:top-1/2 before:left-0 before:h-5 before:w-1 before:-translate-y-1/2 before:rounded-full before:bg-[color:var(--sidebar-primary)] before:content-[""]'
                          : 'text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground',
                      )
                    }
                  >
                    <Icon className="size-4 shrink-0" aria-hidden="true" />
                    {itemLabel}
                  </NavLink>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>

      <div className="border-sidebar-border text-sidebar-foreground/50 border-t px-5 py-4 text-xs">
        Alto Palermo · Travels &amp; Tours
        <span className="mt-1 block">v{APP_VERSION}</span>
      </div>
    </nav>
  )
}
